import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import logo from "../logo.png";

function getIstDateString() {
  return new Date().toLocaleDateString("en-GB", {
    timeZone: "Asia/Kolkata",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export default function LimitsPage() {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalCountry, setModalCountry] = useState("");
  const [modalType, setModalType] = useState(""); // "INCREASE" or "DECREASE"
  const [remark, setRemark] = useState("");
  const [saving, setSaving] = useState(false);

  const navigate = useNavigate();
  const todayIst = getIstDateString();

  // fetchLimits can be reused after managing
  const fetchLimits = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/auth/api/otp/allLimit");
      setData(res.data.data);
    } catch {
      setError("Failed to load data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLimits();
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    navigate("/login");
  };

  // open the remark modal
  const openModal = (countryCode, type) => {
    setModalCountry(countryCode);
    setModalType(type);
    setRemark("");
    setModalOpen(true);
  };

  const closeModal = () => {
    if (!saving) setModalOpen(false);
  };

  // called when user clicks "Save" in modal
  const handleSaveRemark = async () => {
    if (!remark.trim()) {
      alert("Remark is required.");
      return;
    }
    setSaving(true);
    try {
      await api.request({
        method: "POST",
        url: "/auth/api/otp/manageLimit",
        data: { countryCode: modalCountry, type: modalType, remark },
      });
      closeModal();
      await fetchLimits();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to update limit.");
    } finally {
      setSaving(false);
    }
  };

  // sort & filter
  const sorted = data.slice().sort((a, b) => b.limit - a.limit);
  const filtered = sorted.filter(
    (item) =>
      item.countryCode.includes(search) ||
      item.countryName.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="container-lg">
        <p>Loading…</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="container-lg">
        <p className="error-message">{error}</p>
        <button onClick={() => navigate("/login")}>Back to Login</button>
      </div>
    );
  }

  return (
    <div className="container-lg">
      {/* Header */}
      <div className="header-bar">
        <img src={logo} alt="Logo" className="logo" />
        <button className="logout" onClick={handleLogout}>
          Logout
        </button>
      </div>

      {/* Heading */}
      <h2 className="page-heading">OTP Limits for {todayIst} (IST)</h2>

      {/* Search */}
      <div className="search-container">
        <input
          type="text"
          className="table-search"
          placeholder="Search by code or country…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Table */}
      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Country Code</th>
              <th>Country Name</th>
              <th className="col-total-limit">Total Limit</th>
              <th className="col-limit-left">Limit Left</th>
              <th className="action-header">Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(({ countryCode, countryName, totalLimit, limit }) => (
              <tr key={countryCode}>
                <td>{countryCode}</td>
                <td>{countryName}</td>
                <td className="col-total-limit">{totalLimit}</td>
                <td className="col-limit-left">{limit}</td>
                <td className="action-cell">
                  <button
                    className="action-btn"
                    onClick={() => openModal(countryCode, "INCREASE")}
                  >
                    +
                  </button>
                  <button
                    className="action-btn"
                    onClick={() => openModal(countryCode, "DECREASE")}
                  >
                    −
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan="5" className="no-data">
                  No matches found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ─── Remark Modal ─────────────────────────────────────────────────────── */}
      {modalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              {modalType === "INCREASE"
                ? `Increase remark for ${modalCountry}`
                : `Decrease remark for ${modalCountry}`}
            </div>
            <textarea
              className="modal-input"
              rows={4}
              placeholder="Enter your remark…"
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              disabled={saving}
            />
            <div className="modal-buttons">
              <button
                className="btn-cancel"
                onClick={closeModal}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className="btn-save"
                onClick={handleSaveRemark}
                disabled={saving}
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
